import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  FormDialog,
  IMAGE_FILE_ACCEPT,
  RhfFileInput,
  RhfSelect,
  RhfTextField,
} from '@core/components';
import type {
  CreateVestmentData,
  UpdateVestmentData,
  Vestment,
} from '../../api/vestment';
import { createVestment, updateVestment } from '../../api/vestment';
import { AlbType } from '../enum/albType';
import { VestmentCategory } from '../enum/vestmentCategory';
import {
  fromCategoryAndSubtype,
  getSubtype,
  isV1VestmentType,
} from '../enum/vestmentType';
import { getImageThumbnailUrl } from '../utils/image';
import {
  VESTMENT_SUBTYPE_OPTIONS_BY_CATEGORY,
  getSubtypeLabel,
} from '../utils/vestment';

export type EditableVestmentCategory =
  | VestmentCategory.Alb
  | VestmentCategory.Mitre;

interface VestmentCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  category: EditableVestmentCategory;
  vestment?: Vestment | null;
  onSuccess?: () => void;
}

type VestmentFormValues = {
  name: string;
  subtype: AlbType | null;
  image: File | null;
};

const VestmentCategoryDialog: React.FC<VestmentCategoryDialogProps> = ({
  open,
  onClose,
  category,
  vestment,
  onSuccess,
}) => {
  const singular = category === VestmentCategory.Alb ? 'Alb' : 'Mitre';
  const isAlb = category === VestmentCategory.Alb;
  const subtypeOptions = useMemo(() => {
    const options = VESTMENT_SUBTYPE_OPTIONS_BY_CATEGORY[VestmentCategory.Alb];
    const current = vestment ? getSubtype(vestment.type) : null;
    if (
      !vestment ||
      !current ||
      !isV1VestmentType(vestment.type) ||
      options.some((option) => option.value === current)
    ) {
      return options;
    }
    return [...options, { value: current, label: getSubtypeLabel(current) }];
  }, [vestment]);
  const defaultValues = useMemo<VestmentFormValues>(() => {
    const subtype = vestment ? getSubtype(vestment.type) : null;
    return {
      name: vestment?.name ?? '',
      subtype: isAlb && subtype ? (subtype as AlbType) : null,
      image: null,
    };
  }, [isAlb, vestment]);

  const onSubmit = async (data: VestmentFormValues) => {
    const type = fromCategoryAndSubtype(category, isAlb ? data.subtype : null);
    if (vestment) {
      const payload: UpdateVestmentData = {
        name: data.name.trim(),
        type,
      };
      if (data.image) {
        payload.image = data.image;
      }
      await updateVestment(vestment.id, payload);
      toast.success(`${singular} updated successfully`);
    } else {
      const payload: CreateVestmentData = {
        name: data.name.trim(),
        type,
        image: data.image as File,
      };
      await createVestment(payload);
      toast.success(`${singular} created successfully`);
    }
    onSuccess?.();
  };

  return (
    <FormDialog<VestmentFormValues>
      open={open}
      onClose={onClose}
      title={vestment ? `Edit ${singular}` : `Add ${singular}`}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={vestment ? 'Update' : 'Add'}
      cancelText="Cancel"
    >
      <RhfTextField<VestmentFormValues> name="name" label="Name" required />
      {isAlb ? (
        <RhfSelect<VestmentFormValues>
          name="subtype"
          label="Type"
          required
          options={subtypeOptions}
        />
      ) : null}
      <RhfFileInput<VestmentFormValues>
        name="image"
        label="Image"
        accept={IMAGE_FILE_ACCEPT}
        required={!vestment}
        initialPreviewSrc={getImageThumbnailUrl(vestment)}
      />
    </FormDialog>
  );
};

export default VestmentCategoryDialog;
